import { useState, useMemo, Dispatch, SetStateAction } from 'react';
import {
  SpaceBetween,
  Container,
  Select,
  Checkbox,
  CheckboxProps,
  Textarea,
  Input,
} from '@amzn/awsui-components-react';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Button from '@amzn/awsui-components-react/polaris/button';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import {
  ambassadorRegistrationPossibleCountries,
  ambassadorRegistrationPossibleLanguages,
  ambassadorRegistrationPossibleSiteTypes,
} from '../../data/ambassadorRegistrationOptions';

export interface AmbassadorRegistrationFormValues {
  language: OptionDefinition | null;
  country: OptionDefinition | null;
  city: OptionDefinition | null;
  siteType: OptionDefinition | null;
  siteCode: OptionDefinition | null;
  businessUnit: OptionDefinition | null;
  siteRepresentation: boolean;
  teamRepresentation: boolean;
  employeeLedGroupRepresentation: boolean;
  howDidYouHear: OptionDefinition | null;
  howDidYouHearSpecification: string;
  myManagerHasApprovedThisRole: boolean;
  sendMeACopy: boolean;
  teamLocation?: OptionDefinition | null;
  teamName?: string | undefined;
  teamSize?: number | undefined;
  teamLeadEmail?: string | undefined;
  ergName?: string[] | undefined;
  ergLocation?: OptionDefinition | null;
  siteLead?: string | undefined;
}

export interface AmbassadorRegistrationFormProps {
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
  onSubmit: () => void;
  loading: boolean;
}

export function AmbassadorRegistrationForm({
  formValues,
  setFormValues,
  onSubmit,
  loading,
}: AmbassadorRegistrationFormProps) {
  // these will get pulled from endpoint eventually (https://sim.amazon.com/issues/cce-6286)
  const [fieldsEditing, setFieldsEditing] = useState<string[]>([]);

  const { t } = useTranslation();

  const howDidYouHearOptions = [
    { label: t('ambassadorRegistration.ACIWiki'), value: '1' },
    { label: t('ambassadorRegistration.togetherAtAmazon'), value: '2' },
    { label: t('ambassadorRegistration.existingCommunityAmbassadorReferral'), value: '3' },
    { label: t('ambassadorRegistration.internalcommunications'), value: '4' },
    { label: t('ambassadorRegistration.aToZ'), value: '5' },
    { label: t('ambassadorRegistration.leadershipRecommendation'), value: '6' },
    { label: t('ambassadorRegistration.otherPleaseSpecify'), value: '7' },
  ];

  const isValid = useMemo(() => {
    const allSelectsChosen =
      formValues.language?.label && formValues.siteType?.label && formValues.howDidYouHear?.label;

    const representationChosen =
      (formValues.siteRepresentation && formValues.siteLead) ||
      (formValues.teamRepresentation &&
        formValues.teamLocation?.label &&
        formValues.teamName &&
        formValues.teamSize) ||
      (formValues.employeeLedGroupRepresentation &&
        formValues.ergName &&
        formValues.ergLocation?.label);

    return allSelectsChosen && representationChosen && formValues.myManagerHasApprovedThisRole;
  }, [formValues]);

  return (
    <>
      <SpaceBetween direction="vertical" size="m">
        <Container>
          <SpaceBetween direction="vertical" size="m">
            <h2>{t('ambassadorRegistration.personalDetails')}</h2>

            <FormField
              stretch
              label={t('ambassadorRegistration.preferredLanguage')}
              info={
                <Button
                  onClick={() =>
                    setFieldsEditing((currentlyEditing) => [
                      ...currentlyEditing,
                      'preferredLanguage',
                    ])
                  }
                  variant="inline-link"
                >
                  {t('ambassadorRegistration.edit')}
                </Button>
              }
            >
              <Select
                selectedOption={formValues.language}
                disabled={!fieldsEditing.includes('preferredLanguage')}
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, language: detail.selectedOption }))
                }
                options={ambassadorRegistrationPossibleLanguages}
                placeholder={t('ambassadorRegistration.selectOption')}
              />
            </FormField>

            <h2>{t('ambassadorRegistration.workDetails')}</h2>
            <FormField
              stretch
              label={t('ambassadorRegistration.siteType')}
              info={
                <Button
                  onClick={() =>
                    setFieldsEditing((currentlyEditing) => [...currentlyEditing, 'siteType'])
                  }
                  variant="inline-link"
                >
                  {t('ambassadorRegistration.edit')}
                </Button>
              }
            >
              <Select
                selectedOption={formValues.siteType}
                disabled={!fieldsEditing.includes('siteType')}
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, siteType: detail.selectedOption }))
                }
                options={ambassadorRegistrationPossibleSiteTypes}
                placeholder={t('ambassadorRegistration.selectOption')}
              />
            </FormField>

            <SpaceBetween direction="vertical" size="xxxs">
              <h2 style={{ marginBottom: 0 }}>{t('ambassadorRegistration.representation')}</h2>
              <span>{t('ambassadorRegistration.moreThanOne')}</span>
            </SpaceBetween>

            <RepresentationCheckbox
              checked={formValues.siteRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, siteRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.site')}
              description={t('ambassadorRegistration.siteDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <RepresentationCheckbox
              checked={formValues.teamRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, teamRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.team')}
              description={t('ambassadorRegistration.teamDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <RepresentationCheckbox
              checked={formValues.employeeLedGroupRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, employeeLedGroupRepresentation: detail.checked }))
              }
              title={t('ambassadorRegistration.employeeLedGroup')}
              description={t('ambassadorRegistration.employeeLedGroupDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />

            <h3>{t('ambassadorRegistration.howDidYouHear')}</h3>
            <FormField stretch>
              <Select
                selectedOption={formValues.howDidYouHear}
                placeholder={t('ambassadorRegistration.selectOption')}
                data-testid="how-did-you-hear-dropdown"
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, howDidYouHear: detail.selectedOption }))
                }
                options={howDidYouHearOptions}
              />
            </FormField>
            {formValues.howDidYouHear?.label === t('ambassadorRegistration.otherPleaseSpecify') && (
              <Textarea
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, howDidYouHearSpecification: detail.value }))
                }
                value={formValues.howDidYouHearSpecification}
                placeholder={t('ambassadorRegistration.pleaseSpecify')}
                data-testid="ambassador-registration-specify-other-way-to-hear"
              />
            )}
            <Checkbox
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, myManagerHasApprovedThisRole: detail.checked }))
              }
              checked={formValues.myManagerHasApprovedThisRole}
            >
              {t('ambassadorRegistration.myManagerHasApprovedThisRole')}
            </Checkbox>
            <Checkbox
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, sendMeACopy: detail.checked }))
              }
              checked={formValues.sendMeACopy}
            >
              {t('ambassadorRegistration.sendMeACopy')}
            </Checkbox>
          </SpaceBetween>
        </Container>

        <div>
          <Button variant="primary" disabled={!isValid} loading={loading} onClick={onSubmit}>
            {t('ambassadorRegistration.submitApplication')}
          </Button>
        </div>
      </SpaceBetween>
    </>
  );
}

export const RepresentationCheckbox = ({
  checked,
  title,
  description,
  onChange,
  formValues,
  setFormValues,
}: {
  checked: boolean;
  title: string;
  description: string;
  onChange: NonCancelableEventHandler<CheckboxProps.ChangeDetail> | undefined;
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
}) => {
  const { t } = useTranslation();

  return (
    <FormField stretch>
      <div
        className="ambassador-representation-checkbox"
        data-testid="ambassador-representation-checkbox"
      >
        <SpaceBetween size="s" direction="vertical">
          <Checkbox onChange={onChange} checked={checked} description={description}>
            {title}
          </Checkbox>
          {checked && title === t('ambassadorRegistration.site') && (
            <FormField stretch label={t('ambassadorRegistration.siteLead')}>
              <Input
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, siteLead: detail.value }))
                }
                value={formValues.siteLead || ''}
                placeholder={t('ambassadorRegistration.siteLead')}
                data-testid="ambassador-registration-specify-other-way-to-hear"
              />
            </FormField>
          )}
          {checked && title === t('ambassadorRegistration.team') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.teamLocation')}>
                <Select
                  selectedOption={formValues.teamLocation || null}
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamLocation: detail.selectedOption }))
                  }
                  options={ambassadorRegistrationPossibleCountries}
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.teamName')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamName: detail.value }))
                  }
                  value={formValues.teamName || ''}
                  placeholder={t('ambassadorRegistration.teamName')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.teamSize')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamSize: parseInt(detail.value) }))
                  }
                  value={`${formValues.teamSize}` || ''}
                  inputMode="numeric"
                  type="number"
                  placeholder={t('ambassadorRegistration.teamSize')}
                />
              </FormField>
            </>
          )}
          {checked && title === t('ambassadorRegistration.employeeLedGroup') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.ergName')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergName: [detail.value] }))
                  }
                  value={formValues.ergName?.[0] || ''}
                  placeholder={t('ambassadorRegistration.ergName')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.ergLocation')}>
                <Select
                  selectedOption={formValues.ergLocation || null}
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergLocation: detail.selectedOption }))
                  }
                  options={ambassadorRegistrationPossibleCountries}
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              </FormField>
            </>
          )}
        </SpaceBetween>
      </div>
    </FormField>
  );
};
